import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import type { DraftPending, Severity, TriageItem } from "@/lib/types";
import { DraftCard } from "@/components/DraftCard";
import { TriageItemView } from "@/components/TriageItem";
import { Empty } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useRealtime } from "@/lib/realtime";
import { useToasts } from "@/components/ui/toast";

const SEV_ORDER: Record<Severity, number> = { urgent: 0, warn: 1, info: 2 };

export function Triage() {
  const qc = useQueryClient();
  const { push } = useToasts();
  const triageQ = useQuery({
    queryKey: ["triage"],
    queryFn: () => api.triage({ limit: 200 }),
    refetchInterval: 30_000,
  });
  const draftsQ = useQuery({
    queryKey: ["drafts"],
    queryFn: () => api.drafts({ limit: 200 }),
    refetchInterval: 30_000,
  });

  // urgent realtime nudge — toast on insert into pager_log
  useRealtime("pager_log", (p) => {
    const row = p.new as { message?: string; severity?: string };
    push({
      tone: row.severity === "urgent" ? "danger" : "warning",
      title: row.message?.slice(0, 80) ?? "Pager alert",
      duration: 8000,
    });
    qc.invalidateQueries({ queryKey: ["triage"] });
  });
  useRealtime("drafts_pending", () => {
    qc.invalidateQueries({ queryKey: ["drafts"] });
    qc.invalidateQueries({ queryKey: ["triage"] });
  });

  const items = (triageQ.data ?? []).slice().sort(
    (a, b) =>
      SEV_ORDER[a.severity] - SEV_ORDER[b.severity] ||
      new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime(),
  );

  const draftsById = React.useMemo(() => {
    const m = new Map<string, DraftPending>();
    for (const d of draftsQ.data ?? []) m.set(d.draft_id, d);
    return m;
  }, [draftsQ.data]);

  const groups = groupBySeverity(items);

  // keyboard nav: j/k cycles focus; cmd-A approves the focused draft.
  const [focused, setFocused] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!items.length) {
      setFocused(null);
      return;
    }
    if (!focused || !items.some((i) => i.id === focused)) setFocused(items[0].id);
  }, [items, focused]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const tag = e.target.tagName.toLowerCase();
        if (tag === "textarea" || tag === "input") return;
      }
      if (e.key !== "j" && e.key !== "k") return;
      e.preventDefault();
      const idx = items.findIndex((i) => i.id === focused);
      const next =
        e.key === "j"
          ? Math.min(items.length - 1, idx + 1)
          : Math.max(0, idx - 1);
      setFocused(items[next]?.id ?? null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, focused]);

  const isLoading = triageQ.isLoading || draftsQ.isLoading;

  return (
    <div className="space-y-4">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Triage</h1>
          <p className="text-xs text-muted-foreground">
            Things that want your attention, ranked. {items.length} item
            {items.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="hidden text-2xs text-muted-foreground md:inline">
            <span className="kbd">J</span>/<span className="kbd">K</span> nav ·{" "}
            <span className="kbd">⌘A</span> approve · <span className="kbd">⌘D</span> deny
          </span>
          <Button
            variant="ghost"
            size="iconSm"
            onClick={() => {
              triageQ.refetch();
              draftsQ.refetch();
            }}
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
        </div>
      </header>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Empty
          title="All clear."
          icon={<CheckCircle2 className="text-success" />}
          hint="Nothing needs your attention. Kitt will ping when something does."
        />
      ) : (
        <div className="space-y-6">
          {(["urgent", "warn", "info"] as Severity[]).map((sev) =>
            groups[sev].length ? (
              <section key={sev}>
                <SectionHeader severity={sev} count={groups[sev].length} />
                <div className="space-y-2">
                  {groups[sev].map((item) => {
                    if (item.kind === "draft_pending") {
                      const draft = draftsById.get(item.id);
                      // If we don't have rich draft data yet, fall back to the
                      // generic renderer; once drafts load, we re-render rich.
                      if (draft) {
                        return (
                          <DraftCard
                            key={item.id}
                            draft={draft}
                            focused={focused === item.id}
                            onAfterAction={() => setFocused(null)}
                          />
                        );
                      }
                    }
                    return (
                      <TriageItemView
                        key={`${item.kind}:${item.id}`}
                        item={item}
                        focused={focused === item.id}
                      />
                    );
                  })}
                </div>
              </section>
            ) : null,
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ severity, count }: { severity: Severity; count: number }) {
  const tone =
    severity === "urgent"
      ? "text-destructive"
      : severity === "warn"
        ? "text-warning"
        : "text-muted-foreground";
  return (
    <div className="mb-2 flex items-center gap-2 text-xs uppercase tracking-wide">
      <span className={tone}>● {severity}</span>
      <span className="text-muted-foreground">·</span>
      <span className="text-muted-foreground">{count}</span>
    </div>
  );
}

function groupBySeverity(items: TriageItem[]): Record<Severity, TriageItem[]> {
  const out: Record<Severity, TriageItem[]> = { urgent: [], warn: [], info: [] };
  for (const i of items) out[i.severity].push(i);
  return out;
}
